<script>
  // A Default Want List card's options (spec "Wants" > Card options modal),
  // opened by tapping the card: which printing and finish the want is for,
  // its Card Specificity (CONTEXT.md), in either direction. The finish chips
  // (Any and the three Finishes), the set dropdown (SetSelect: every set the
  // card was printed in, newest first, with a search box), and the printings
  // as an image grid: the card's 40 newest with no set picked, every
  // printing in the set once one is. Tapping a printing pins it; tapping it
  // again widens to its set. What another choice rules out is greyed in its
  // place, never hidden (a finish greys printings within the grid, it never
  // refills it). The pure rules are `card-options-logic.js`.
  //
  // Every pick applies at once: it shows straight away (`pending`) and
  // `onChange` hands it to the page, which writes the picks in order, even
  // after the modal has closed. Once the latest pick's write is done the
  // modal shows what the entry holds (the pick, or, refused, what it was).
  // The dialog itself (focus, Escape, backdrop, action row) is shared/Dialog.
  import Dialog from '../shared/Dialog.svelte';
  import SetSelect from './SetSelect.svelte';
  import CardPills from '../shared/CardPills.svelte';
  import { getCardImageUrl } from '../../lib/services/card-utils.js';
  import { fetchCardSets, fetchCardPrintings } from '../../lib/services/wantlist-api.js';
  import {
    specOf,
    finishChips,
    printingTiles,
    pickSet,
    pickPrinting,
    pickFinish,
  } from '../../lib/logic/card-options-logic.js';

  /** @typedef {import('../../lib/logic/card-options-logic.js').Spec} Spec */
  /** @typedef {import('../../lib/logic/card-options-logic.js').PrintingTile} PrintingTile */
  /** @typedef {import('../../lib/types.js').Printing} Printing */

  /**
   * @type {{
   *   entry: import('../../lib/types.js').WantEntry,
   *   onChange: (spec: Spec) => Promise<void>,
   *   onRemove: () => void,
   *   onClose: () => void,
   * }}
   */
  let { entry, onChange, onRemove, onClose } = $props();

  // The latest pick, on screen ahead of the server (null once its write is
  // done and the entry holds whatever the server kept).
  let pending = $state(/** @type {Spec | null} */ (null));
  let picks = 0;

  const spec = $derived(pending ?? specOf(entry));
  const name = $derived(entry.card_name);

  // The card's sets, read once per card.
  let sets = $state.raw(/** @type {import('../../lib/types.js').CardSet[] | null} */ (null));
  $effect(() => {
    const card = name;
    sets = null;
    let live = true;
    fetchCardSets(card).then((res) => {
      if (live) sets = res.ok ? (res.data?.sets ?? []) : [];
    });
    return () => {
      live = false;
    };
  });

  // The grid's printings: the newest 40, or the picked set's. Each read is
  // kept for this card, so going back to a set shows it at once.
  /** @type {Map<string, Printing[]>} */
  const read = new Map();
  let rows = $state.raw(/** @type {Printing[] | null} */ (null));
  let failed = $state(false);
  const gridSet = $derived(spec.set_code);
  $effect(() => {
    const card = name;
    const set = gridSet;
    const key = `${card}\n${set ?? ''}`;
    const kept = read.get(key) ?? null;
    failed = false;
    rows = kept;
    if (kept) return;
    let live = true;
    fetchCardPrintings(card, set).then((res) => {
      if (!live) return;
      if (res.ok) {
        read.set(key, res.data);
        rows = res.data;
      } else {
        failed = true;
      }
    });
    return () => {
      live = false;
    };
  });

  const tiles = $derived(rows ? printingTiles(spec, rows) : []);
  const chips = $derived(finishChips(spec, sets, tiles));

  /** @param {Spec | null} next */
  async function choose(next) {
    if (!next) return;
    const mine = ++picks;
    pending = next;
    try {
      await onChange(next);
    } finally {
      if (mine === picks) pending = null;
    }
  }

  /** @param {PrintingTile} t */
  const tileLabel = (t) => `${t.setName} #${t.cn}${t.variant ? ` (${t.variant})` : ''}`;
</script>

<Dialog title={name} width={640} {onClose}>
  <div class="options">
    <div class="finishes" role="radiogroup" aria-label="Finish">
      {#each chips as c (c.label)}
        <button
          class="chip"
          class:on={c.on}
          role="radio"
          aria-checked={c.on}
          disabled={c.disabled}
          data-autofocus={c.on ? '' : undefined}
          onclick={() => choose(pickFinish(spec, c.key))}
        >
          {#if c.key === 'foil'}<span class="foil-dot" aria-hidden="true"></span>{/if}{c.label}
        </button>
      {/each}
    </div>

    <SetSelect {spec} {sets} onPick={(code) => choose(pickSet(spec, code))} />

    <div class="grid">
      {#if failed}
        <p class="note">Couldn't load the printings.</p>
      {:else if !rows}
        <div class="prints" aria-label="Loading printings">
          {#each [0, 1, 2, 3, 4, 5] as i (i)}<span class="ghost"></span>{/each}
        </div>
      {:else}
        <div class="prints">
          {#each tiles as t (t.key)}
            <button
              class="print"
              class:on={t.on}
              aria-pressed={t.on}
              aria-label={tileLabel(t)}
              title={tileLabel(t)}
              disabled={t.disabled}
              onclick={() => choose(pickPrinting(spec, t))}
            >
              <span class="art">
                <img
                  src={getCardImageUrl({ scryfallId: t.scryfallId }, 'normal')}
                  alt=""
                  loading="lazy"
                  width="488"
                  height="680"
                />
              </span>
              <CardPills price={t.price} foil={spec.finish === 'foil'} />
            </button>
          {/each}
        </div>
      {/if}
    </div>
  </div>

  {#snippet foot()}
    <button class="remove" onclick={onRemove}>Remove card</button>
    <button class="done" onclick={onClose}>Done</button>
  {/snippet}
</Dialog>

<style>
  .options {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 6px 20px 0;
  }
  .finishes {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 34px;
    padding: 0 14px;
    border: 1px solid var(--color-border);
    border-radius: 999px;
    background: var(--color-bg-surface);
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-secondary);
    cursor: pointer;
  }
  .chip:active:not(:disabled) {
    background: var(--color-accent-bg);
  }
  .chip.on {
    border-color: var(--color-accent-dark);
    background: var(--color-accent-dark);
    color: #fff;
  }
  /* Never printed in it (or not in the picked set): greyed, not pickable. */
  .chip:disabled {
    opacity: 0.35;
    cursor: default;
  }
  .foil-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--foil-gradient);
  }
  /* The printings scroll down inside the dialog, never sideways; the chips
     and the set stay put above them. */
  .grid {
    max-height: min(52dvh, 520px);
    overflow-y: auto;
    margin: 0 -20px;
    padding: 6px 20px 12px;
  }
  .prints {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
    gap: 14px 10px;
  }
  .ghost {
    aspect-ratio: 488 / 680;
    border-radius: 0.5rem;
    background: var(--color-accent-bg);
  }
  .print {
    position: relative;
    padding: 0;
    border: none;
    border-radius: 0.5rem;
    background: none;
    cursor: pointer;
  }
  .print:active:not(:disabled) {
    transform: scale(0.98);
  }
  .art {
    display: block;
    overflow: hidden;
    aspect-ratio: 488 / 680;
    border-radius: 0.5rem;
    background: var(--color-bg-tray);
  }
  .art img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  /* Not made in the picked finish: in its place, greyed, not pickable. */
  .print:disabled {
    opacity: 0.3;
    filter: grayscale(0.8);
    cursor: default;
  }
  /* The pinned printing wears the dark ring. */
  .print.on {
    box-shadow:
      0 0 0 2px var(--color-bg-surface),
      0 0 0 4.5px var(--color-accent-dark);
  }
  .note {
    margin: 1rem 0;
    text-align: center;
    color: var(--color-text-tertiary);
  }
  @media (max-width: 768px) {
    .prints {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }
</style>
